import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface TeamMember {
  id: string;
  name: string;
  title: string;
  credentials: string | null;
  bio: string;
  photo_url: string;
  linkedin_url: string | null;
  display_order: number;
  is_active: boolean;
}

interface TeamMemberManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: TeamMember | null;
  onSuccess: () => void;
}

export function TeamMemberManagementDialog({
  open,
  onOpenChange,
  member,
  onSuccess,
}: TeamMemberManagementDialogProps) {
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    credentials: '',
    bio: '',
    photo_url: '',
    linkedin_url: '',
    display_order: 0,
    is_active: true,
  });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (member) {
      setFormData({
        name: member.name,
        title: member.title,
        credentials: member.credentials || '',
        bio: member.bio,
        photo_url: member.photo_url,
        linkedin_url: member.linkedin_url || '',
        display_order: member.display_order,
        is_active: member.is_active,
      });
    } else {
      setFormData({
        name: '',
        title: '',
        credentials: '',
        bio: '',
        photo_url: '',
        linkedin_url: '',
        display_order: 0,
        is_active: true,
      });
    }
  }, [member, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const dataToSave = {
        name: formData.name,
        title: formData.title,
        credentials: formData.credentials || null,
        bio: formData.bio,
        photo_url: formData.photo_url,
        linkedin_url: formData.linkedin_url || null,
        display_order: formData.display_order,
        is_active: formData.is_active,
      };

      if (member) {
        const { error } = await supabase
          .from('team_members')
          .update(dataToSave)
          .eq('id', member.id);

        if (error) throw error;

        toast({
          title: 'Success',
          description: 'Team member updated successfully',
        });
      } else {
        const { error } = await supabase
          .from('team_members')
          .insert(dataToSave);

        if (error) throw error;

        toast({
          title: 'Success',
          description: 'Team member added successfully',
        });
      }

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {member ? 'Edit Team Member' : 'Add Team Member'}
          </DialogTitle>
          <DialogDescription>
            {member
              ? 'Update the team member details below.'
              : 'Add a new team member to the leadership section.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., John Smith"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Founder & CEO"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="credentials">Credentials</Label>
            <Input
              id="credentials"
              value={formData.credentials}
              onChange={(e) => setFormData({ ...formData, credentials: e.target.value })}
              placeholder="e.g., CISSP, CISO | MSc Cyber Security"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bio">Biography *</Label>
            <Textarea
              id="bio"
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              placeholder="Write a brief biography..."
              rows={4}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="photo_url">Photo URL *</Label>
            <Input
              id="photo_url"
              value={formData.photo_url}
              onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
              placeholder="e.g., /team/john-smith.jpg"
              required
            />
            <p className="text-xs text-muted-foreground">
              Path to the team member's photo (e.g., /team/john-smith.jpg)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="linkedin_url">LinkedIn URL</Label>
            <Input
              id="linkedin_url"
              value={formData.linkedin_url}
              onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
              placeholder="e.g., https://linkedin.com/in/johnsmith"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="display_order">Display Order</Label>
              <Input
                id="display_order"
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                min={0}
              />
              <p className="text-xs text-muted-foreground">
                Lower numbers appear first
              </p>
            </div>

            <div className="space-y-2">
              <Label>Visibility</Label>
              <div className="flex items-center gap-2 pt-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <span className="text-sm text-muted-foreground">
                  {formData.is_active ? 'Visible on website' : 'Hidden from website'}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : member ? 'Update' : 'Add'} Team Member
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}